import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { Icon, Text, UnstyledButton } from '@myrealtrip/web-ui';
import { useEffect } from 'react';
import useToast from '~hooks/useToast';

interface Props {
  currentValue: number;
  onIncrease: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDecrease: (e: React.MouseEvent<HTMLButtonElement>) => void;
  min?: number;
  max?: number;
}

const TravelerCountStepper = ({ currentValue = 0, onIncrease, onDecrease, min, max }: Props) => {
  const theme = useTheme();
  const toast = useToast({ position: 'bottom-center' });

  const decreaseStepDisabled = min ? currentValue <= min : false;
  const increaseStepDisabled = max ? currentValue >= max : false;

  useEffect(() => {
    if (increaseStepDisabled) {
      toast.success({
        id: 'max-travelers',
        message: '최대 9명까지 예약 가능합니다.',
      });
    }
  }, [currentValue]);

  return (
    <Container isAlert={currentValue === 0}>
      <StepButton type="button" disabled={decreaseStepDisabled} onClick={onDecrease}>
        <Icon
          name="IcoMinus"
          size={20}
          color={(() => {
            if (decreaseStepDisabled) {
              return theme.colors.gray.s300;
            }
            if (currentValue === 0) {
              return theme.colors.red.s500;
            }
            return theme.colors.blue.s500;
          })()}
        />
      </StepButton>
      <InputWrapper>
        <Text
          typography="paragraphNormal16"
          color={currentValue === 0 ? theme.colors.red.s500 : theme.colors.gray.s1000}
        >
          {currentValue}
        </Text>
      </InputWrapper>
      <StepButton type="button" disabled={increaseStepDisabled} onClick={onIncrease}>
        <Icon
          name="IcoPlus"
          size={20}
          color={(() => {
            if (increaseStepDisabled) {
              return theme.colors.gray.s300;
            }
            if (currentValue === 0) {
              return theme.colors.red.s500;
            }
            return theme.colors.blue.s500;
          })()}
        />
      </StepButton>
    </Container>
  );
};

const Container = styled.div<{ isAlert: boolean }>`
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  width: 104px;
  height: 48px;
  border: 1px solid
    ${({ theme, isAlert }) => (isAlert ? theme.colors.red.s500 : theme.colors.blue.s500)};
  border-radius: 60px;
  padding: 0px 10px;
  > *:not(:last-of-type) {
    margin-right: 2px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 100%;
  height: 100%;
`;

const StepButton = styled(UnstyledButton)<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.8)};
  }

  &:active {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

export default TravelerCountStepper;
